import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Check, X, Clock, RefreshCw, Send, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const IncomingConsignment = () => {
  const navigate = useNavigate();
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState({ open: false, entryId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  const isReceived = (c) => ['received', 'accepted'].includes(c.status);
  const pendingList = consignments.filter((c) => !isReceived(c));
  const receivedList = consignments.filter(isReceived);
  const visibleConsignments = activeTab === 'received' ? receivedList : pendingList;

  const fetchConsignments = async () => {
    try {
      setLoading(true);
      const data = await greigeStorageApi.getIncomingConsignments();
      setConsignments(data.consignments || []);
    } catch (error) {
      toast.error('Failed to fetch consignments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsignments();
  }, []);

  const handleRequest = async (entryId) => {
    try {
      setProcessing(entryId);
      await greigeStorageApi.requestConsignment(entryId);
      toast.success('Consignment requested - waiting for shipment confirmation');
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request');
    } finally {
      setProcessing(null);
    }
  };

  const handleAccept = async (entryId) => {
    try {
      setProcessing(entryId);
      await greigeStorageApi.acceptConsignment(entryId);
      toast.success('Consignment accepted - Ready for bundle receipt');
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      setProcessing(rejectDialog.entryId);
      await greigeStorageApi.rejectConsignment(rejectDialog.entryId, rejectReason);
      toast.success('Consignment rejected');
      setRejectDialog({ open: false, entryId: null });
      setRejectReason('');
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ready: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Ready - Not Requested' },
      requested: { color: 'bg-orange-100 text-orange-700', icon: Send, label: 'Requested - Awaiting Shipment' },
      shipped: { color: 'bg-blue-100 text-blue-700', icon: Truck, label: 'Shipped - Pending Receipt' },
      pending_acceptance: { color: 'bg-blue-100 text-blue-700', icon: Truck, label: 'Shipped - Pending Receipt' },
      accepted: { color: 'bg-green-100 text-green-700', icon: Check, label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-700', icon: X, label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.ready;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const renderActions = (consignment) => {
    const status = consignment.status;
    const entryId = consignment.entry_id;
    
    if (status === 'ready') {
      return (
        <div className="flex gap-3">
          <Button
            onClick={() => handleRequest(entryId)}
            disabled={processing === entryId}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid={`request-${entryId}`}
          >
            <Send className="w-4 h-4 mr-2" />
            Request Consignment
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectDialog({ open: true, entryId })}
            disabled={processing === entryId}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-${entryId}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      );
    }
    
    if (status === 'requested') {
      return (
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-orange-600 border-orange-300">
            <Clock className="w-3 h-3 mr-1" />
            Waiting for Inward to confirm shipment
          </Badge>
        </div>
      );
    }
    
    if (status === 'shipped' || status === 'pending_acceptance') {
      return (
        <div className="flex gap-3">
          <Button
            onClick={() => handleAccept(entryId)}
            disabled={processing === entryId}
            className="bg-[#2D5F4F] hover:bg-[#1A1F2E]"
            data-testid={`accept-${entryId}`}
          >
            <Check className="w-4 h-4 mr-2" />
            Accept & Receive
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectDialog({ open: true, entryId })}
            disabled={processing === entryId}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-${entryId}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Incoming Consignment</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage Section</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchConsignments}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-2 mb-6 border-b border-[#E5DFD0]">
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'pending' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Pending
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'pending' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-pending-count">{pendingList.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('received')}
            data-testid="tab-received"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'received' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Received Slips
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'received' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-received-count">{receivedList.length}</span>
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : visibleConsignments.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">
                {activeTab === 'received' ? 'No Received Slips' : 'No Pending Consignments'}
              </h3>
              <p className="text-[#5A5F6E]">
                {activeTab === 'received'
                  ? 'No consignments have been received yet for Greige Storage section'
                  : 'There are no pending consignments for Greige Storage section'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {visibleConsignments.map((consignment) => (
              <Card key={consignment.entry_id} className="border-[#E5DFD0]" data-testid={`consignment-${consignment.entry_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-display">{consignment.document_barcode || consignment.entry_id}</CardTitle>
                    {getStatusBadge(consignment.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Entry ID</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.entry_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Bundles</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.bundle_count || consignment.bundles?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Vendor</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.vendor_name || consignment.source_section || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">
                        {consignment.status === 'shipped' ? 'Shipped At' : consignment.status === 'requested' ? 'Requested At' : 'Created At'}
                      </p>
                      <p className="font-medium text-[#1A1F2E]">
                        {consignment.shipped_at 
                          ? new Date(consignment.shipped_at).toLocaleString('en-IN')
                          : consignment.requested_at
                            ? new Date(consignment.requested_at).toLocaleString('en-IN')
                            : consignment.created_at
                              ? new Date(consignment.created_at).toLocaleString('en-IN')
                              : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {renderActions(consignment)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, entryId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Consignment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Input
              id="reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-2"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, entryId: null })}>
              Cancel
            </Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-button">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncomingConsignment;
